/**
 * Date helpers shared by all pages.
 *
 * Every function accepts `null`/`undefined`/invalid input and returns a
 * placeholder instead of "Invalid Date". Functions that depend on the current
 * time take an optional `now` so they can be tested deterministically.
 */

const LOCALE = 'en-US';
const MS_PER_MINUTE = 60_000;
const MS_PER_DAY = 24 * 60 * MS_PER_MINUTE;

/**
 * Placeholder shown for missing dates
 */
const EMPTY_DATE = '-';

export type DateInput = string | number | Date | null | undefined;

/**
 * Parse a date input; returns null for missing or invalid values
 */
export function parseDate(value: DateInput): Date | null {
  if (value === null || value === undefined || value === '') return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function toTimestamp(now: DateInput): number {
  return parseDate(now ?? Date.now())?.getTime() ?? Date.now();
}

/**
 * Date only, e.g. "Jan 5, 2026"
 */
export function formatDate(value: DateInput, placeholder = EMPTY_DATE): string {
  const date = parseDate(value);
  if (!date) return placeholder;
  return date.toLocaleDateString(LOCALE, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Date and time, e.g. "Jan 5, 2026, 02:30 PM"
 */
export function formatDateTime(value: DateInput, placeholder = EMPTY_DATE): string {
  const date = parseDate(value);
  if (!date) return placeholder;
  return date.toLocaleDateString(LOCALE, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Compact date and time without year, e.g. "Jan 5, 02:30 PM"
 */
export function formatShortDateTime(value: DateInput, placeholder = EMPTY_DATE): string {
  const date = parseDate(value);
  if (!date) return placeholder;
  return date.toLocaleString(LOCALE, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Relative time in the past: "Just now", "5m ago", "3h ago", "2d ago",
 * falling back to a short date ("Jan 5") after a week.
 */
export function formatRelativeTime(
  value: DateInput,
  options: { now?: DateInput; placeholder?: string } = {}
): string {
  const { now, placeholder = 'Never' } = options;
  const date = parseDate(value);
  if (!date) return placeholder;

  const diffMs = toTimestamp(now) - date.getTime();
  const diffMins = Math.floor(diffMs / MS_PER_MINUTE);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;

  return date.toLocaleDateString(LOCALE, { month: 'short', day: 'numeric' });
}

/**
 * Whole days until the given date (rounded up); negative when in the past.
 * Returns null for missing/invalid dates.
 */
export function getDaysUntil(value: DateInput, now?: DateInput): number | null {
  const date = parseDate(value);
  if (!date) return null;
  return Math.ceil((date.getTime() - toTimestamp(now)) / MS_PER_DAY);
}

/**
 * Time of day with seconds, e.g. "02:30:05 PM"
 */
export function formatTimeOfDay(value: DateInput, placeholder = EMPTY_DATE): string {
  const date = parseDate(value);
  if (!date) return placeholder;
  return date.toLocaleTimeString(LOCALE, { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

/**
 * Compact time-axis label for buckets of `intervalSeconds`: the 24-hour time
 * for sub-hour buckets ("14:05"), the day otherwise ("Jan 5")
 */
export function formatAxisTime(value: DateInput, intervalSeconds: number, placeholder = EMPTY_DATE): string {
  const date = parseDate(value);
  if (!date) return placeholder;
  if (intervalSeconds < 3600) {
    return date.toLocaleTimeString(LOCALE, { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });
  }
  return date.toLocaleDateString(LOCALE, { month: 'short', day: 'numeric' });
}
